import { AnimatedGaugeChart } from '@/components/ui/AnimatedGaugeChart'
import {
  MarketIndicatorBaseProps,
  NO_READING,
  formatSpread,
  spreadBarWidth,
} from './MarketIndicatorBase'
import { InfoTooltip } from '@/components/ui/info-tooltip'

export const MarketIndicatorMobile = ({ 
  buyerPercentage = null,
  sentiment = null,
  priceSpread = null,
  dealSpread = null,
  loading = false
}: MarketIndicatorBaseProps) => {
  if (loading) {
    return (
      <div className="h-full flex flex-col relative">
        <p 
          className="absolute font-plus-jakarta font-medium leading-[24px] left-[23px] text-[#2d2923] dark:text-[#fdfdfd] text-[16px] top-[23px] uppercase"
          
        >
          <span className="inline-flex items-center gap-2">
            Market indicator
            <InfoTooltip content="A snapshot of buy vs sell interest and spreads on the Secondaries order book." ariaLabel="Market indicator info" />
          </span>
        </p>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            Loading market data...
          </div>
        </div>
      </div>
    )
  }

  // Calculate progress bar width (clamped to 100%)
  const priceSpreadWidth = spreadBarWidth(priceSpread)
  const dealSpreadWidth = spreadBarWidth(dealSpread)

  const minHeight = 505

  return (
    <div className="relative w-full" style={{ minHeight: `${minHeight}px`, height: '100%' }}>
      <p 
        className="absolute font-plus-jakarta font-medium leading-[24px] left-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-[16px] top-[24px] uppercase"
        
      >
        <span className="inline-flex items-center gap-2">
          Market indicator
          <InfoTooltip content="A snapshot of buy vs sell interest and spreads on the Secondaries order book." ariaLabel="Market indicator info" />
        </span>
      </p>

      <div className="absolute left-[24px] top-[64px] w-[208px] h-[208px] z-10">
        <AnimatedGaugeChart
          value={buyerPercentage ?? 0}
          max={100}
          min={0}
          gaugePrimaryColor="#e3fd5a"
          gaugeSecondaryColor="#ffffff"
          className="w-full h-full"
        />
      </div>

      <p 
        className="absolute font-plus-jakarta font-normal leading-[24px] left-[200px] text-[#2d2923] dark:text-[#fdfdfd] text-[16px] top-[72px] w-[153px] whitespace-pre-wrap"
        
      >
        Market sentiment indicator for buyers/sellers ratio
      </p>

      <p 
        className="absolute font-plus-jakarta font-normal leading-[48px] left-[200px] text-[#2d2923] dark:text-[#fdfdfd] text-[28px] top-[160px] tracking-[-0.52px]"
        
      >
        {sentiment ?? NO_READING}
      </p>

      <p 
        className="absolute font-plus-jakarta font-medium leading-[20px] left-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px] top-[192px]"
        
      >
        Buyer
      </p>

      <p 
        className="absolute font-plus-jakarta font-medium leading-[20px] left-[173px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px] text-right top-[192px] translate-x-[-100%]"
        
      >
        Seller
      </p>

      <div className="absolute left-[16px] right-[16px] top-[240px] flex flex-col gap-[12px]">
        {/* Price Spread Card */}
        <div className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-[12px] p-[16px]">
          <p 
            className="font-plus-jakarta font-semibold leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]"
            
          >
            Price Spread
          </p>
          <div className="flex flex-col gap-[6px]">
            <div className="flex items-center justify-between">
              <p 
                className="font-plus-jakarta font-medium leading-[28px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]"
                
              >
                <span className="text-[20px]">{formatSpread(priceSpread)} </span>
                <span className="text-[12.9px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
              </p>
              <div className="h-[28px] overflow-clip relative w-[172px] shrink-0">
                {/* Background pattern */}
                <div 
                  className="absolute h-[20px] right-0 top-[4px] w-[177px] bg-white dark:bg-[#1c1c1c]" 
                  style={{
                    backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(45,41,35,0.12) 1px, rgba(45,41,35,0.12) 2px)'
                  }}
                />
                {/* Progress bar */}
                <div 
                  className="absolute bg-[#e3fd5a] h-[20px] left-0 top-[4px]"
                  style={{ width: `${priceSpreadWidth}%` }}
                />
              </div>
            </div>
            <p 
              className="font-plus-jakarta font-normal leading-[16px] text-[12px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-pre-wrap"
              
            >
              Difference between buy and sell prices
            </p>
          </div>
        </div>

        {/* Deal Size-Spread Card */}
        <div className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-[12px] p-[16px]">
          <p 
            className="font-plus-jakarta font-semibold leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]"
            
          >
            Deal Size-Spread
          </p>
          <div className="flex flex-col gap-[6px]">
            <div className="flex items-center justify-between">
              <p 
                className="font-plus-jakarta font-medium leading-[28px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]"
                
              >
                <span className="text-[20px]">{formatSpread(dealSpread)}</span>
                <span className="text-[20px]"> </span>
                <span className="text-[12.9px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
              </p>
              <div className="h-[28px] overflow-clip relative w-[172px] shrink-0">
                {/* Background pattern */}
                <div 
                  className="absolute h-[20px] right-0 top-[4px] w-[177px] bg-white dark:bg-[#1c1c1c]" 
                  style={{
                    backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(45,41,35,0.12) 1px, rgba(45,41,35,0.12) 2px)'
                  }}
                />
                {/* Progress bar */}
                <div 
                  className="absolute bg-[#e3fd5a] h-[20px] left-0 top-[4px]"
                  style={{ width: `${dealSpreadWidth}%` }}
                />
              </div>
            </div>
            <p 
              className="font-plus-jakarta font-normal leading-[16px] text-[12px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-pre-wrap"
              
            >
              Difference between buy and sell volume
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
